import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import HomeOverlay from "../components/HomeOverlay";
import Footer from "../components/Footer";
import { useScrolled } from "../hooks";
import { GAMES } from "../data/gamesData";
import "../styles/games.css";

const GENRES = ["Genres", ...Array.from(new Set(GAMES.map((g) => g.genre)))];

const SORTS = [
  { key: "name", label: "Name" },
  { key: "name-asc", label: "Name A → Z" },
  { key: "name-desc", label: "Name Z → A" },
];

const RELEASES = [
  { key: "release", label: "Release" },
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
];

/* Parse "duration" -> timestamp (dùng mốc cuối nếu là khoảng) */
const MONTHS = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

const parseReleaseDate = (duration) => {
  if (!duration || typeof duration !== "string") return null;

  const parts = duration.split("-");
  const raw = parts[parts.length - 1].trim();

  const match = raw.match(/^([A-Za-z]{3,})\s+(\d{4})$/);
  if (!match) return null;

  const monthKey = match[1].slice(0, 3).toLowerCase();
  const month = MONTHS[monthKey];
  const year = Number(match[2]);

  if (month === undefined || Number.isNaN(year)) return null;
  return new Date(year, month, 1).getTime();
};

/* ============ DROPDOWN ============ */
const Dropdown = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);

  React.useEffect(() => {
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const currentLabel =
    options.find((o) => (o.key ?? o) === value)?.label ?? value;

  return (
    <div className="dd" onClick={(e) => e.stopPropagation()}>
      <button className="dd-btn" onClick={() => setOpen((o) => !o)}>
        <span className="dd-text">{currentLabel}</span>
        <span className={`dd-chev${open ? " open" : ""}`}>▼</span>
      </button>
      {open && (
        <div className="dd-menu">
          {options.map((opt) => {
            const key = opt.key ?? opt;
            const lbl = opt.label ?? opt;
            return (
              <div
                key={key}
                className={`dd-item${key === value ? " active" : ""}`}
                onClick={() => {
                  onChange(key);
                  setOpen(false);
                }}
              >
                {lbl}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ============ PAGE ============ */
const GamesPage = () => {
  const navigate = useNavigate();
  const scrolled = useScrolled(500);

  const [search, setSearch] = useState("");
  const [genre, setGenre] = useState("Genres");
  const [sort, setSort] = useState("name");
  const [release, setRelease] = useState("release");

  // Đổi Sort By -> reset Release về mặc định
  const handleSortChange = (val) => {
    setSort(val);
    setRelease("release");
  };

  // Đổi Release -> reset Sort By về mặc định
  const handleReleaseChange = (val) => {
    setRelease(val);
    setSort("name");
  };

  const filtered = useMemo(() => {
    let list = GAMES.slice();

    if (genre !== "Genres") list = list.filter((g) => g.genre === genre);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((g) => g.title.toLowerCase().includes(q));
    }

    // Sort theo tên (chỉ khi sort đang bật)
    if (sort === "name-asc") {
      list.sort((a, b) => a.title.localeCompare(b.title));
    } else if (sort === "name-desc") {
      list.sort((a, b) => b.title.localeCompare(a.title));
    }

    // Sort theo thời gian phát hành (chỉ khi release đang bật)
    if (release === "newest" || release === "oldest") {
      list.sort((a, b) => {
        const ta = parseReleaseDate(a.duration);
        const tb = parseReleaseDate(b.duration);
        if (ta === null && tb === null) return 0;
        if (ta === null) return 1;
        if (tb === null) return -1;
        return release === "newest" ? tb - ta : ta - tb;
      });
    }

    return list;
  }, [search, genre, sort, release]);

  return (
    <main id="portfolio" className={`games-main${scrolled ? " scrolled" : ""}`}>
      <HomeOverlay />
      <Navigation />

      <section className="lib">
        <div className="container">
          <div className="lib-head">
            <div className="lib-label">
              <span className="lib-name">Game Library</span>
            </div>

            <div className="lib-tools">
              <div className="lib-search">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.5" y2="16.5" />
                </svg>
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <Dropdown options={GENRES} value={genre} onChange={setGenre} />
              <Dropdown options={SORTS} value={sort} onChange={handleSortChange} />
              <Dropdown options={RELEASES} value={release} onChange={handleReleaseChange} />
            </div>
          </div>

          <div className="lib-count">
            Showing <b>{filtered.length}</b> / {GAMES.length} games
          </div>

          {filtered.length === 0 ? (
            <div className="lib-empty">
              <span>No Match</span>
              <p>No games found. Try a different keyword or filter.</p>
            </div>
          ) : (
            <div className="lib-grid">
              {filtered.map((g) => (
                <article
                  key={g.id}
                  className="lib-card"
                  onClick={() => navigate(`/games/${g.id}`)}
                >
                  <span className="lib-card-genre">{g.genre}</span>
                  <div className="lib-card-media">
                    <img src={g.img} alt={g.title} loading="lazy" />
                  </div>
                  <div className="lib-card-body">
                    <h3>{g.title}</h3>
                    <div className="lib-card-cta">
                      <span>Click to play</span>
                      <span className="arrow">→</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default GamesPage;